/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 31-08-2026
 *
 * De centrale backendlaag van ng21 (golf a1): één getypeerde methode per backendactie, plus de
 * notify-stream waarop de rest van de applicatie meeluistert. Alles wat met het backend praat
 * loopt hierlangs; de ruwe `send` blijft bewust binnen deze service.
 *
 * Het transport is sinds golf a6 de sihw-ngx `SpinJS`: deze service bezit de socket.
 */
import {Injectable, inject} from '@angular/core';
import {filter, map, Subject, type Observable} from 'rxjs';
import {Sihwlog, SpinJS, type VerzendOpties} from 'sihw-ngx';
import type {KanaalIntern, KanaalNotify} from './notify.types';
import type {AanmeldResultaat, LoginGegevens, LoginResultaat, Userdata} from './userdata.types';
import type {Dashboardv1Modelinfo} from '../../services/dashboardv1.service';
import type {Dashboardv2ActiedataWire, Dashboardv2ModelWire} from '../../services/dashboardv2.service';
import type {ModelDocument} from '../model/model-serialisatie';

/**
 * Het id van een model. Een uuid, geen getal: de kolom `model.idmodel` is `char(36)`. De laag stond
 * sinds golf a1 op `number`, wat aan de runtime niets veranderde — javascript geeft de string
 * gewoon door — maar wel elke ng21-consument een verkeerd type gaf. Rechtgezet in golf a3, waar de
 * opslagketen het id als eigen state ging bijhouden.
 */
export type ModelId = string;

/**
 * Eén stap uit de teacher-replay zoals het backend hem per stuk teruggeeft: de snapshot-json van die
 * stap en de weergavestand (`meta`) die op dat moment gold. `simstatus`/`simfeedback` reizen mee in de
 * snapshot en worden door de replay als `SimulatieStatus` uitgepakt.
 */
export interface Replaystap {
    /**
     * De bewaarde stand. Sinds golf a5 stap 3 draagt hij ook de feedbackblokken; de `ReplayService`
     * leest hem als `Modelsnapshot`. Hier bewust de ruwe vorm, zodat deze transportlaag niet aan de
     * opslagketen hoeft te hangen.
     */
    snapshot: ModelDocument & {simstatus?: unknown; simfeedback?: unknown; [veld: string]: unknown};
    meta: {mn: number; sp: number; fp: number | boolean};
}

/**
 * De uitslag van een simulatieverzoek. Bij een fout levert het backend (of deze service) een
 * melding in plaats van simulatiedata.
 */
export interface SimulatieResultaat {
    result?: boolean;
    simulatie?: any;
    melding?: string;

    /**
     * Wat het backend met dit verzoek deed (golf 3.6): `klaar` als de simulatie er staat, `gestart` als
     * dit verzoek het rekenen in gang zette, `bezig` als er al aan gewerkt werd, of `fout`. Sinds
     * 17-09-2026 zit de simulatie alleen nog in het antwoord van `simulatieOphalen`.
     */
    status?: 'klaar' | 'gestart' | 'bezig' | 'fout';

    /** De hash van de invoer waar deze simulatie bij hoort; gelijke hash betekent dezelfde simulatie. */
    invoerhash?: string | null;

    /** Sinds wanneer er gerekend wordt (epoch in ms), bij `bezig`. */
    sinds?: number;

    /** Bij een fout: wat de simulator meldde (`fout`, `timeout`, `gestaakt`). */
    simulatiestatus?: string | null;

    /**
     * Het foutveld dat `simulate` bij een mislukte call zet (overgenomen uit de ng1-implementatie).
     * De `SimulatieService` test erop; `status: 'fout'` zegt hetzelfde, dus samenvoegen kan.
     */
    resultaat?: boolean;

    [veld: string]: any;
}

/**
 * Hoe lang `login` en `aanmeldenCode` op een antwoord wachten (ms), in plaats van de 140 s die de
 * `SpinJS` standaard aanhoudt. Het loginscherm houdt zijn knoppen dicht zolang zo'n aanroep loopt; een
 * aanroep die blijft hangen mag het scherm dus niet langer dan dit blokkeren.
 */
const AANMELD_WACHTTIJD = 15000;

@Injectable({providedIn: 'root'})
export class ApiService {

    private readonly log = new Sihwlog('info', 'ApiService');

    /**
     * Het transport naar het backend.
     */
    private readonly kanaal = inject(SpinJS);

    /**
     * De backend-notificaties. `SpinJS` levert zijn stroom als abonnement in plaats van als
     * observable, dus die wordt hier één keer afgenomen en doorgezet.
     */
    private readonly notifies = new Subject<KanaalNotify>();

    /**
     * De verbindingsgebeurtenissen van het kanaal, om dezelfde reden een eigen stroom.
     */
    private readonly interneEvents = new Subject<KanaalIntern>();

    /**
     * Een id dat deze client in een samenwerking identificeert. Het backend adresseert op
     * clientId, maar de deelnemers herkennen elkaars berichten hieraan (`originele_afzender`),
     * dus alles in deze client gebruikt deze ene waarde.
     */
    readonly unique = `${Date.now().toString(36)}-${Math.floor(Math.random() * Number.MAX_SAFE_INTEGER).toString(36)}`;

    /* ******** Het kanaal ******** */

    constructor() {
        this.kanaal.onNotify((notify: KanaalNotify) => this.notifies.next(notify));
        this.kanaal.onInternal((intern: KanaalIntern) => this.interneEvents.next(intern));
    }

    /**
     * Is er op dit moment verbinding met het backend?
     */
    get verbonden(): boolean {
        return this.kanaal.connected;
    }

    /**
     * De url waarop het backend bereikbaar is.
     */
    get url(): string {
        return this.kanaal.url;
    }

    /**
     * De authkey van de lopende sessie, of `null`. Schrijven slaat hem ook op.
     */
    get authKey(): string | null {
        return this.kanaal.authKey;
    }

    set authKey(authkey: string | null) {
        this.kanaal.authKey = authkey;
    }

    /**
     * Is er een authkey bekend? Zegt niets over de geldigheid ervan.
     */
    get heeftAuth(): boolean {
        return this.kanaal.hasAuth;
    }

    /**
     * Verwijdert de authkey; verzendingen gaan daarna zonder auth.
     */
    verwijderAuth(): void {
        this.kanaal.deleteAuth();
    }

    /**
     * Resolvet zodra het transport zichzelf heeft opgezet: de configuratie is gelezen en de socket
     * bestaat. Nadrukkelijk niet hetzelfde als verbonden zijn — een verzending mag juist vóór de
     * verbinding vertrekken, socket.io buffert hem tot de handshake rond is.
     */
    gereed(): Promise<void> {
        return this.kanaal.whenReady();
    }

    /**
     * Resolvet zodra er verbinding is met het backend.
     */
    whenConnected(): Promise<void> {
        return this.kanaal.whenConnected();
    }

    /**
     * De stroom met de backend-notificaties van één soort.
     * @param msg de notify-naam, bijvoorbeeld `chat` of `clientnames`
     */
    onNotify<T = any>(msg: string): Observable<T> {
        return this.notifies.pipe(filter(notify => notify.msg === msg), map(notify => notify.data as T));
    }

    /**
     * De stroom met álle backend-notificaties, inclusief hun naam. Voor wie ze allemaal wil zien, zoals
     * het testharnas (`harnas.mjs`, `samenwerking3.mjs`).
     */
    onNotifies(): Observable<KanaalNotify> {
        return this.notifies.asObservable();
    }

    /**
     * De stroom met verbindingsgebeurtenissen van het kanaal (`connect`, `spinjsconnect`,
     * `error`, `close`). Voedt de verbindingsspiegel in `SamenwerkingService`.
     */
    onIntern(): Observable<KanaalIntern> {
        return this.interneEvents.asObservable();
    }

    /* ******** Sessie ******** */

    /**
     * Meldt de gebruiker aan. Het backend antwoordt met een authkey en userdata, of met de
     * domein-/projectkeuze die nog gemaakt moet worden.
     */
    login(gegevens: LoginGegevens): Promise<LoginResultaat> {
        return this.send('user', 'login', {...gegevens}, {timeout: AANMELD_WACHTTIJD});
    }

    /**
     * Vernieuwt de lopende sessie op basis van de opgeslagen authkey.
     */
    relogin(): Promise<{ authkey: string; userdata: Userdata }> {
        return this.send('user', 'relogin', {});
    }

    /**
     * Vraagt het backend of iemand met een projectcode binnen kan komen. Zonder
     * bevestigingscode is dit de aanvraag; mét bevestigingscode, wachtwoord en displaynaam is
     * het de afronding waarbij het account wordt aangemaakt.
     */
    aanmeldenCode(code: string, email: string, taal: string,
                  bevestigingscode?: string, password?: string, displaynaam?: string): Promise<AanmeldResultaat> {
        return this.send('user', 'aanmelden_code', {
            code,
            email,
            bevestigingscode,
            password,
            displaynaam,
            lang: taal
        }, {timeout: AANMELD_WACHTTIJD});
    }

    /**
     * Logt in met een lesdirect-sleutel uit material: een directe login op één project.
     */
    lesdirectLogin(key: string): Promise<AanmeldResultaat> {
        return this.send('user', 'aanmelden_lesdirect', {key});
    }

    /**
     * Stapt over naar een ander project van dezelfde gebruiker; levert een nieuwe authkey en
     * nieuwe userdata.
     */
    switchProject(projectId: number | string): Promise<{ authkey?: string; userdata?: Userdata }> {
        return this.send('user', 'switchProject', {project_id: projectId});
    }

    /**
     * Laat het backend een wachtwoordlink mailen. Het backend meldt niet of het adres bestaat.
     */
    stuurWachtwoordlink(email: string, url: string, taal: string): Promise<any> {
        return this.send('user', 'wwlink', {email, url, lang: taal});
    }

    /**
     * Vraagt of een wachtwoordlink nog geldig is.
     */
    async isWachtwoordlink(token: string): Promise<boolean> {
        return (await this.send('user', 'verifyWwlink', {token})).result;
    }

    /**
     * Zet een nieuw wachtwoord op basis van een geldige wachtwoordlink.
     */
    async updateWachtwoord(token: string, nieuwWachtwoord: string): Promise<boolean> {
        return (await this.send('user', 'updateWachtwoord', {token, ww: nieuwWachtwoord})).result;
    }

    /**
     * Slaat een js-object onder een sleutel op bij de ingelogde gebruiker.
     */
    async saveUserData(key: string, jsobject: any): Promise<boolean> {
        const res = await this.send('user', 'saveData', {key, data: jsobject});
        if (!res.succes) {
            return Promise.reject(false);
        }
        return true;
    }

    /**
     * Haalt het onder een sleutel opgeslagen js-object van de ingelogde gebruiker op.
     */
    async haalUserData(key: string): Promise<any> {
        return (await this.send('user', 'haalData', {key})).data;
    }

    /* ******** Model ******** */

    /**
     * Slaat het model op. De opties zijn de save-payload van de opslagketen.
     */
    async saveModel(options: { [key: string]: any }): Promise<any> {
        try {
            return await this.send('model', 'save', options);
        } catch (e) {
            this.log.error('er is iets fout gegaan bij het opslaan van het model', e);
            throw e;
        }
    }

    /**
     * Stuurt een afbeelding van het model als base64-string (zonder data-url-voorvoegsel).
     */
    saveModelImage(modelid: ModelId, img: string, modelversie: number): Promise<any> {
        return this.send('model', 'saveImage', {id: modelid, image: img, versie: modelversie});
    }

    /**
     * Slaat de tellingen van het model en van de norm op.
     */
    saveNormTelling(modelid: ModelId, telling: any, normtelling: any): Promise<any> {
        return this.send('model', 'saveNormTelling', {
            model: modelid,
            telling: {model: telling, norm: normtelling}
        });
    }

    /**
     * Logt een actie los van het opslaan van het model. Bedoeld voor gebaren die wel in de
     * actielog horen maar bij de leider niets veranderen. Geeft het id van de geschreven regel
     * terug.
     */
    logAction(modelId: ModelId | null, action: string, actionArgs?: any, target?: string,
              targettype?: string | null, volger?: string | null): Promise<{ id: number }> {
        return this.send('model', 'logAction', {
            model_id: modelId,
            action,
            arguments: actionArgs || {},
            target,
            targettype,
            volger,
            timestamp: Date.now()
        });
    }

    /**
     * Schrijft een snapshot: een screenshot van een rusttoestand van het model. Het backend doet
     * dezelfde controles als het opslagpad en slaat een stand die gelijk is aan de vorige stap niet
     * nog eens op. De call vertrekt op het moment van aanroepen; het wachten op het antwoord komt
     * daarna.
     * @param modelId het model van deze client
     * @param snapshot de snapshot als json-string
     * @param volgorde het volgnummer dat voor deze stand geclaimd is
     * @param simhash de invoerhash van de simulatie in deze stand, of `null` als er niet gesimuleerd werd
     * @returns of het backend hem heeft opgeslagen, en onder welk nummer — dat kan een ander zijn dan
     *   het geclaimde, want het backend beslist
     */
    async schrijfSnapshot(modelId: ModelId, snapshot: string, volgorde: number,
                          simhash: string | null): Promise<{gelukt: boolean; volgorde: number | null}> {
        //een object-antwoord komt bij SpinJS op het hoogste niveau binnen, net als bij `snapshots`
        return await this.send('model', 'schrijfSnapshot', {model_id: modelId, snapshot, volgorde, simhash});
    }

    /**
     * Haalt de id's op van de snapshots die samen de replay van dit model (of zijn primary) vormen,
     * nieuwste eerst: index 0 is de laatste stap, waar de replay op opent. De snapshots zelf komen per
     * stuk met `haalSnapshot`; zo hoeft een model met honderden stappen niet in één antwoord over de
     * lijn.
     * @param model modelid van de client
     */
    async haalSnapshotlijst(model: ModelId): Promise<number[]> {
        return (await this.send('model', 'snapshots', {model})).ids;
    }

    /**
     * Haalt één replaystap op: de snapshot-json plus de meta (modelleerniveau, simulatievoorkeur,
     * snelste pad) die bij die stap hoorde. Een id dat niet bij dit model hoort of intussen is
     * opgeruimd levert een reject op (backend `NotFound`).
     * @param model modelid van de client
     * @param id het id van de snapshot
     */
    async haalSnapshot(model: ModelId, id: number): Promise<Replaystap> {
        return this.send('model', 'snapshot', {model, id});
    }

    /**
     * Haalt de modellen van de ingelogde gebruiker op.
     * @param [meekijken] haal juist de modellen op die voor meekijken openstaan
     */
    async getAllModels(meekijken?: boolean): Promise<any[]> {
        try {
            return (await this.send('model', meekijken ? 'meekijkmodellen' : 'all', {})).models;
        } catch (e) {
            this.log.error('er is iets fout gegaan bij het ophalen van alle modellen', e);
            throw e;
        }
    }

    /**
     * Haalt de templates en normmodellen op die als nieuw model geladen kunnen worden. Geeft
     * `false` als het backend de lijst weigert.
     */
    async getAllTemplates(): Promise<any[] | false> {
        try {
            const res = await this.send('model', 'templates', {});
            return res.result ? res.models : false;
        } catch (e) {
            this.log.error('Er is iets fout gegaan bij het ophalen van de templates', e);
            throw e;
        }
    }

    /**
     * Haalt één model op om te laden.
     * @param modelid het model, of een versleutelde toegangskey
     * @param [isAdminKey] true als `modelid` zo'n toegangskey is
     */
    async getModelById(modelid: ModelId, isAdminKey?: boolean): Promise<any> {
        try {
            return (await this.send('model', 'get', {id: modelid, adminKey: isAdminKey})) || false;
        } catch (e) {
            this.log.error('er is iets fout gegaan bij het ophalen van het model', e);
            throw e;
        }
    }

    /**
     * Verwijdert het model.
     */
    async deleteModel(modelid: ModelId): Promise<boolean> {
        return (await this.send('model', 'del', {id: modelid})).result;
    }

    /**
     * Laat het backend zorgen dat de simulatie van dit model er is. Het antwoord draagt de simulatie
     * niet (sinds 17-09-2026): bij `klaar` staat hij bij de meegegeven `invoerhash` klaar voor
     * `simulatieOphalen`, bij `gestart`/`bezig` komt hij daar nog.
     *
     * @param modelid het model; het backend regelt zelf dat er van een primary gesimuleerd wordt
     * @param [force] laat het backend de simulatie opnieuw bouwen
     * @param [simtimeout] wordt sinds golf 3.6 niet meer gebruikt: het request wacht nergens meer op.
     *        Blijft staan zolang de `SimulatieService` en `a6.mjs` hem meegeven
     */
    async simulate(modelid: ModelId, force?: boolean, simtimeout?: number | null): Promise<SimulatieResultaat> {
        this.log.debug(`Verzoek om simulatie ${force ? 'geforceerd' : ''}`);
        try {
            //geen verruimde timeout meer: sinds golf 3.6 antwoordt `start` meteen met `klaar`,
            //`gestart` of `bezig`, en wacht niemand nog op de simulator. De parameter blijft staan
            //zolang de aanroepers hem meegeven
            return await this.send('simulate', 'start', {id: modelid, force: !!force});
        } catch (error: any) {
            this.log.error('fout bij simuleren', error);
            return {
                resultaat: false,
                status: 'fout',
                simulatiestatus: Array.isArray(error?.args) ? error.args[0] : null,
                melding: `Fout: ${error?.code || error}`
            };
        }
    }

    /**
     * Haalt de bewaarde simulatie van dit model bij deze invoerhash op, uitgepakt. Rekent nooit: staat
     * hij er niet, dan is het antwoord `null` — nog niet klaar, mislukt, of opgeruimd.
     * @param modelid het model; bij een sub mag het eigen id, het backend zoekt het primary
     * @param hash de invoerhash, uit `simulate`, een `simstatus` of een snapshot
     */
    async simulatieOphalen(modelid: ModelId, hash: string): Promise<unknown | null> {
        let res: any;
        try {
            res = await this.send('simulate', 'ophalen', {id: modelid, hash});
        } catch (error: any) {
            if (error?.code === 'NOT_FOUND') {
                this.log.debug(`geen simulatie bij ${hash}`);
            } else {
                this.log.error('fout bij het ophalen van de simulatie', error);
            }
            return null;
        }
        try {
            return JSON.parse(res.simulatie);
        } catch (e) {
            this.log.error('de opgehaalde simulatie is niet te lezen', e);
            return null;
        }
    }

    /**
     * De lichte probe: rekent er voor dit model een simulatie, en sinds wanneer? Aan de andere kant is
     * dit één geheugen-lookup — geen model, geen prolog, geen runner — dus dit mag als poll van een
     * paar seconden dienen, waar `simulate` de hele cachecheck zou overdoen.
     * @param modelid het model; bij een sub het notifyId (het primary), want daarop sleutelt de runner
     */
    async simulatieBezig(modelid: ModelId): Promise<{bezig: boolean; sinds: number}> {
        try {
            const res: any = await this.send('simulate', 'bezig', {id: modelid});
            return {bezig: !!res?.bezig, sinds: res?.sinds || 0};
        } catch (error) {
            this.log.debug('simulatieBezig mislukt', error);
            return {bezig: false, sinds: 0}; //geen antwoord telt als "er rekent niets"
        }
    }

    /**
     * De invoerhash van dit model zoals het nu is. Haalt geen simulatie op en start niets: het backend
     * bouwt alleen de prolog. Levert `null` als het niet lukt — en dan geldt een bewaarde simulatie dus
     * niet als nog passend.
     * @param modelid het model
     */
    async simulatieInvoerhash(modelid: ModelId): Promise<string | null> {
        try {
            const res: any = await this.send('simulate', 'invoerhash', {id: modelid});
            return res?.invoerhash || null;
        } catch (error) {
            this.log.debug('simulatieInvoerhash mislukt', error);
            return null;
        }
    }

    /* ******** Samenwerken ******** */

    /**
     * Haalt de samenwerkstate van het model op. Slaat meekijken over.
     */
    samenwerkState(modelid: ModelId): Promise<any> {
        return this.send('model', 'samenwerkState', {model: modelid});
    }

    /**
     * Maakt dit model de primary van een nieuwe samenwerking.
     */
    setSamenwerkprimary(modelid: ModelId): Promise<any> {
        return this.send('model', 'setSamenwerkprimary', {model: modelid});
    }

    /**
     * Laat het backend een gekoppeld model maken onder de samenwerking met deze 3x3-code.
     * Levert het primary- en het submodel terug.
     */
    createSamenwerksub(primarycode: string): Promise<{ primary: ModelId; sub: ModelId }> {
        return this.send('model', 'createSamenwerksub', {primarycode});
    }

    /**
     * Ontkoppelt de samenwerking, voor primary én sub.
     */
    ontkoppelSamenwerking(modelid: ModelId): Promise<any> {
        return this.send('model', 'ontkoppelSamenwerking', {model: modelid});
    }

    /**
     * Zet het meekijken met een model op; het backend regelt dat als een samenwerking met een
     * eigen meekijkmodel.
     */
    gaMeekijken(modelid: ModelId): Promise<{ primary: ModelId; sub: ModelId }> {
        return this.send('model', 'gaMeekijken', {model: modelid});
    }

    /**
     * Vraagt of dit model voor meekijken openstaat, en zo ja door wie.
     */
    checkMeekijken(modelid: ModelId): Promise<{ result?: string | false }> {
        return this.send('model', 'checkMeekijken', {model: modelid});
    }

    /**
     * Meldt de samenwerking op dit model aan bij het backend, of af met `null`. Het backend kan
     * dat niet zelf bijhouden omdat de verbinding weg kan vallen; het frontend registreert
     * daarom opnieuw bij elke modelwissel en na elk registratieverzoek.
     * @param modelid het te volgen model, of `null` om af te melden
     * @param [meekijkid] het geopende meekijkmodel
     * @param [replay] draait deze client een replay op dit model? De verdeler maakt zo iemand geen
     *   leider zolang er een ander is die wél kan muteren (§ 5 van `ontwerp-apiservice.md`); het
     *   veld reist mee in de registratie omdat die idempotent is en één call één verdeling geeft
     */
    registreerSamenwerken(modelid: ModelId | null, meekijkid?: ModelId | null, replay?: boolean): Promise<any> {
        return this.send('model', 'registreerSamenwerken', {model: modelid, meekijkid, replay: !!replay});
    }

    /**
     * Stuurt een samenwerkbericht. Van een volger is het een verzoek aan de leider; van een
     * leider is het de melding dat de actie is uitgevoerd.
     * @param brontype `volg` of `leid`
     * @param changeId het id waarmee deze wijziging herkend wordt; alleen van een leider
     * @param lokaalmodel het eigen model
     * @param mastermodel het model waaronder de samenwerking hangt
     * @param origineleAfzender de client die de actie begon
     * @param acties de acties, elk met in elk geval een `actie`-veld
     * @param [extra] meereizende gegevens bij dit bericht
     */
    samenwerkbericht(brontype: string, changeId: string | boolean, lokaalmodel: ModelId | null,
                     mastermodel: ModelId | null, origineleAfzender: string | undefined,
                     acties: any[], extra?: any): Promise<any> {
        return this.send('model', 'samenwerkbericht', {
            brontype,
            changeId,
            lokaalmodel,
            mastermodel,
            originele_afzender: origineleAfzender,
            acties,
            extra
        });
    }

    /**
     * Meldt de undostatus van de leider aan de andere deelnemers.
     */
    samenwerkUndostatus(canundo: boolean, canredo: boolean): Promise<any> {
        return this.send('model', 'samenwerkundostatus', {canUndo: canundo, canRedo: canredo});
    }

    /**
     * Stuurt een bericht voor het gelijk houden van de simulatie tussen de deelnemers.
     */
    simulatiebericht(bericht: any): Promise<any> {
        return this.send('model', 'simulatiebericht', {bericht});
    }

    /* ******** Chat ******** */

    /**
     * Stuurt een chatbericht bij het model.
     * @param tekst het bericht
     * @param notifyId het model waaronder de chat hangt: altijd het hoofdmodel
     */
    chatSend(tekst: string, notifyId: ModelId): Promise<any> {
        return this.send('model', 'chat', {model: notifyId, tekst});
    }

    /**
     * Markeert chatberichten als gelezen.
     */
    chatGelezen(ids: number[]): Promise<any> {
        return this.send('model', 'chatGelezen', {ids});
    }

    /* ******** Dashboards ******** */

    /**
     * Meldt zich aan voor de live-gegevens van het eerste docentendashboard.
     */
    dashboardAbonneer(): Promise<any> {
        return this.send('dashboard', 'subscribe');
    }

    /**
     * Meldt zich aan voor de live-gegevens van het tweede docentendashboard.
     */
    dashboardAbonneerV2(): Promise<any> {
        return this.send('dashboard', 'subscribev2');
    }

    /**
     * Zegt het abonnement op de gegevens van het eerste dashboard op.
     */
    dashboardZegOp(): Promise<any> {
        return this.send('dashboard', 'unsubscribe');
    }

    /**
     * Zegt het abonnement op de gegevens van het tweede dashboard op. Apart van het eerste: sinds
     * golf b0-2 meldt een dashboard dat sluit alleen zichzelf af.
     */
    dashboardZegOpV2(): Promise<any> {
        return this.send('dashboard', 'unsubscribev2');
    }

    /**
     * Haalt naam, eigenaar, afbeelding en normtelling van de opgegeven modellen op. Het backend
     * levert het plaatjespad met beginslash (of `false` als er geen plaatje is); de basis-url is
     * transportkennis, dus die zetten we er hier voor — het dashboard krijgt een bruikbare url.
     */
    async dashboardModelinfo(modellen: string[]): Promise<Record<string, Dashboardv1Modelinfo>> {
        const gevonden = await this.send('dashboard', 'modelinfo', {modellen});
        const basis = this.url.replace(/\/+$/, '');
        for (const info of Object.values(gevonden) as Dashboardv1Modelinfo[]) {
            info.imgUrl = info.imgUrl ? `${basis}${info.imgUrl}` : null;
        }
        return gevonden;
    }

    /**
     * Haalt de modellen op die in de gekozen periode relevant zijn voor het tweede dashboard.
     * @param eind het einde van de periode als epoch in milliseconden
     */
    async dashboardRelevanteModellen(eind: number): Promise<Dashboardv2ModelWire[]> {
        return (await this.send('dashboard', 'v2relevantModels', {end: eind})).models;
    }

    /**
     * Haalt de actiegegevens van de opgegeven modellen op voor het tweede dashboard.
     */
    dashboardActiedata(ids: string[]): Promise<Record<string, Dashboardv2ActiedataWire>> {
        return this.send('dashboard', 'v2actiondata', {ids});
    }

    /* ******** Transport ******** */

    /**
     * Verstuurt een verzoek naar het backend. Bewust private: elke actie heeft hierboven zijn
     * eigen getypeerde methode, zodat de wire-vormen op één plek staan.
     */
    private send(controller: string, action: string, data: { [key: string]: any } = {},
                 opties?: VerzendOpties): Promise<any> {
        return this.kanaal.send(controller, action, data, opties);
    }
}
